/**
 * Description clean-up rules — the screen's pure decisions.
 *
 * The rules themselves run on the server (`applyDescriptionRules`, top to
 * bottom by `sortOrder`) and the live test box asks the server too; the browser
 * never re-implements a pattern match. What is here is the ordering arithmetic
 * and the small facts the screen shows.
 */

export const RULE_KINDS = [
  ['regex', 'Regex'],
  ['text', 'Text — remove exactly'],
];

export const kindLabel = (kind) => (RULE_KINDS.find(([k]) => k === kind)?.[1] ?? kind);

/** Examples shown under the add form, and what each does to a real ABSA description. */
export const RULE_EXAMPLES = [
  { kind: 'regex', pattern: 'POS PURCHASE \\(EFFEC \\d+\\)', sample: 'POS PURCHASE (EFFEC 05082026) Tops' },
  { kind: 'text', pattern: 'CREDIT TRANSFER', sample: 'CREDIT TRANSFER Smith Plumbing' },
  { kind: 'regex', pattern: 'DIGITAL PAYMENT (DT|CR)\\s+\\([^)]*\\)', sample: 'DIGITAL PAYMENT CR (ABSA 4421) Acme' },
];

/** The order the server applies them in: sortOrder, then oldest first. */
export const sortRules = (rules) =>
  [...(rules ?? [])].sort((a, b) =>
    (a.sortOrder - b.sortOrder) || String(a.createdAt).localeCompare(String(b.createdAt)));

/** The spacing a renumber leaves between rules, so a hand-edited order has room. */
export const ORDER_STEP = 10;

/**
 * Move one rule up or down and say what to PATCH.
 *
 * Rules are created with `sortOrder` 0 and so tie, which means swapping two
 * numbers is not enough — the whole list is renumbered `10, 20, 30…` in its new
 * order and only the rules whose number actually changes are returned.
 * `visibleIds` lets a hidden (deactivated) rule stay put: the move is relative to
 * the neighbour the user can SEE, and the hidden ones keep their places between.
 *
 * Returns `[{ id, sortOrder }]`, empty when the rule is already at that end.
 */
export function reorderPatches(rules, id, direction, visibleIds = null) {
  const full = sortRules(rules);
  const visible = visibleIds ? full.filter((r) => visibleIds.has(r.id)) : full;
  const at = visible.findIndex((r) => r.id === id);
  const neighbour = visible[at + (direction === 'up' ? -1 : 1)];
  if (at < 0 || !neighbour) return [];

  const order = full.map((r) => r.id);
  const i = order.indexOf(id);
  const j = order.indexOf(neighbour.id);
  [order[i], order[j]] = [order[j], order[i]];

  const byId = new Map(full.map((r) => [r.id, r]));
  return order
    .map((rid, n) => ({ id: rid, sortOrder: (n + 1) * ORDER_STEP }))
    .filter((p) => byId.get(p.id).sortOrder !== p.sortOrder);
}

/** Is there enough typed to ask the server for a live preview? */
export const canPreview = ({ pattern, sample }) =>
  String(pattern ?? '').trim() !== '' && String(sample ?? '') !== '';

/** `2026-09-01T10:00:00Z` -> `2026-09-01`; never -> "never". */
export const lastHitLabel = (iso) => (iso ? String(iso).slice(0, 10) : 'never');
